import styles from './discussions.module.css'

import * as React from 'react'

import { NOTES_V2_MENTIONS } from '@disco/constants/FeatureTypes'
import { PLAYING } from '@disco/constants/MediaStatus'
import { parseTimestampToSeconds } from '@disco/shared-components/rich-text-editor'
import {
  playTrack,
  seekTrackPosition,
} from '@disco/state-management/actions/MediaActions'
import {
  useGetDiscussionByIdQuery,
  useGetDiscussionsQuery,
} from '@disco/state-management/api/discussions'
import { selectTrackPlaybackStatus } from '@disco/state-management/selectors/media'
import { getConstanceSetting } from '@disco/state-management/selectors/user'
import { pushUINotification } from '@disco/utils/notifications'

import type { DiscussionVisibility } from 'types/discussion'
import type { TrackId } from 'types/track'

import { Box, DiscoCallout, Flex, Spinner, Text } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import CommentEditor from './comment-editor'
import DiscussionComment from './discussion-comment'
import TrackFollowBell from './track-follow-bell'
import DiscussionVisibilityControl from './visibility-control'

export const DISCUSSION_PAGE_SIZE = 100

type Props = {
  trackId: TrackId
  trackDurationSeconds?: number
  visibility: DiscussionVisibility
  onVisibilityChange: (visibility: DiscussionVisibility) => void
  targetDiscussionId?: number
}

const TrackDiscussions = ({
  trackId,
  trackDurationSeconds,
  visibility,
  onVisibilityChange,
  targetDiscussionId,
}: Props): React.ReactElement => {
  const anchorTrackIdRef = React.useRef(trackId)
  const anchorVisibilityRef = React.useRef(visibility)
  const onAnchorTrack =
    targetDiscussionId != null && trackId === anchorTrackIdRef.current
  const activeTargetId =
    onAnchorTrack && visibility === anchorVisibilityRef.current
      ? targetDiscussionId
      : undefined

  const { data, isLoading, isFetching, isError } = useGetDiscussionsQuery(
    {
      target_type: 'track',
      target_object_id: trackId,
      visibility,
      count: DISCUSSION_PAGE_SIZE,
    },
    { refetchOnMountOrArgChange: activeTargetId != null },
  )

  const discussions = data?.results ?? []

  const listAreaRef = React.useRef<HTMLDivElement>(null)

  // Auto-scroll to the newest note the first time a tab is viewed for the
  // current track; on a same-track tab switch (client <-> internal notes)
  // restore whatever scroll position the user left that tab at instead of
  // clobbering it. Navigating to a different track resets this memory so a
  // track's notes still open scrolled to the newest note per SLAM-2182, even
  // if you'd previously scrolled up on it before navigating away. trackId
  // must stay in the effect's deps: the Notes tab isn't remounted when the
  // edit-metadata modal's next/prev track nav swaps trackId in place, and if
  // the new track's discussions are already RTK-Query-cached, isLoading
  // never transitions, so this effect wouldn't otherwise re-run.
  const currentTrackIdRef = React.useRef(trackId)
  const scrollPositionsRef = React.useRef<Map<DiscussionVisibility, number>>(
    new Map(),
  )

  React.useLayoutEffect(() => {
    if (isLoading) return
    const el = listAreaRef.current
    if (!el) return

    if (currentTrackIdRef.current !== trackId) {
      currentTrackIdRef.current = trackId
      scrollPositionsRef.current = new Map()
    }

    el.scrollTop = scrollPositionsRef.current.get(visibility) ?? el.scrollHeight
    scrollPositionsRef.current.set(visibility, el.scrollTop)
  }, [trackId, visibility, isLoading])

  const handleListAreaScroll = React.useCallback(() => {
    const el = listAreaRef.current
    if (!el) return
    scrollPositionsRef.current.set(visibility, el.scrollTop)
  }, [visibility])

  const targetPresent =
    !!activeTargetId &&
    discussions.some(
      d =>
        d.id === activeTargetId ||
        (d.replies ?? []).some(r => r.id === activeTargetId),
    )

  const hasScrolledRef = React.useRef(false)
  React.useEffect(() => {
    if (!onAnchorTrack) hasScrolledRef.current = false
    if (
      isLoading ||
      isFetching ||
      isError ||
      !activeTargetId ||
      !targetPresent ||
      hasScrolledRef.current
    ) {
      return
    }
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-testid="discussion-comment-${activeTargetId}"]`,
      )
      if (!el) return
      hasScrolledRef.current = true
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, isError, onAnchorTrack, activeTargetId, data])

  const targetMissing =
    !isLoading && !isFetching && !isError && !!activeTargetId && !targetPresent
  const {
    error: targetFetchError,
    isError: targetFetchFailed,
    isSuccess: targetFetchSucceeded,
  } = useGetDiscussionByIdQuery(activeTargetId ?? 0, {
    skip: !targetMissing,
  })
  const notFoundNotifiedRef = React.useRef(false)
  React.useEffect(() => {
    if (!onAnchorTrack) notFoundNotifiedRef.current = false
    if (!activeTargetId || !targetMissing || notFoundNotifiedRef.current) return
    if (!targetFetchFailed && !targetFetchSucceeded) return
    notFoundNotifiedRef.current = true
    const status = (targetFetchError as { status?: number } | undefined)?.status
    pushUINotification({
      message:
        targetFetchFailed && status === 404
          ? 'This note is no longer available'
          : 'We couldn’t open that note here',
      level: 'warning',
    })
  }, [
    onAnchorTrack,
    activeTargetId,
    targetMissing,
    targetFetchFailed,
    targetFetchSucceeded,
    targetFetchError,
  ])

  const dispatch = useDispatch()
  const playbackStatus = useSelector(state =>
    selectTrackPlaybackStatus(state, { trackId }),
  )
  const notesMentionsEnabled = useSelector(state =>
    Boolean(getConstanceSetting(state, NOTES_V2_MENTIONS)),
  )

  const handleTimestampClick = React.useCallback(
    (timestamp: string) => {
      // handles both single timestamp and timestamp range to extract the start token
      const startToken = timestamp.split('-')[0]
      if (!startToken) return
      const seconds = parseTimestampToSeconds(startToken)
      if (!Number.isFinite(seconds)) return

      if (playbackStatus !== PLAYING) {
        dispatch(
          playTrack(trackId, 'in-app', { playbackLocation: 'Track comments' }),
        )
      }
      dispatch(seekTrackPosition(trackId, seconds))
    },
    [dispatch, playbackStatus, trackId],
  )

  return (
    <Box width="100%" className={`${styles.container} TrackDiscussionsRoot`}>
      <Flex align="center" gap="2" pt="1" pr="2">
        <Box flexGrow="1" minWidth="0">
          <DiscussionVisibilityControl
            value={visibility}
            onValueChange={onVisibilityChange}
          />
        </Box>
        {/* key by trackId so the bell's local level resets when the modal's
        next/prev nav swaps trackId in place without remounting the panel */}
        {notesMentionsEnabled && (
          <TrackFollowBell key={trackId} trackId={trackId} />
        )}
      </Flex>
      {discussions.length === DISCUSSION_PAGE_SIZE && (
        <Box my="4">
          <DiscoCallout type="info">
            {`Notes are currently limited to displaying the last ${DISCUSSION_PAGE_SIZE} comments`}
          </DiscoCallout>
        </Box>
      )}
      <Box
        mt="3"
        className={styles.listArea}
        ref={listAreaRef}
        onScroll={handleListAreaScroll}
        data-testid="notes-list-area"
      >
        {isLoading ? (
          <Flex align="center" gap="2" p="3">
            <Spinner />
            <Text size="2" color="gray">
              Loading notes…
            </Text>
          </Flex>
        ) : isError ? (
          <Text size="2" color="red" as="p">
            Failed to load notes. Please try again.
          </Text>
        ) : discussions.length === 0 ? (
          <Text size="2" color="gray" as="p">
            No notes yet.
          </Text>
        ) : (
          <Flex direction="column" gap="3" className={styles.list}>
            {[...discussions].reverse().map(d => (
              <DiscussionComment
                key={d.id}
                discussion={d}
                trackDurationSeconds={trackDurationSeconds}
                onTimestampClick={handleTimestampClick}
                autoFocusReply={
                  !isFetching &&
                  !!activeTargetId &&
                  (d.id === activeTargetId ||
                    (d.replies ?? []).some(r => r.id === activeTargetId))
                }
              />
            ))}
          </Flex>
        )}
      </Box>

      <Box pt="3" className={styles.editorWrapper}>
        <CommentEditor
          key={visibility}
          mode="create"
          trackId={trackId}
          visibility={visibility}
          trackDurationSeconds={trackDurationSeconds}
        />
      </Box>
    </Box>
  )
}

export default TrackDiscussions
